import md5 from "md5"
import { FLOOR_GROUND } from "~/constants/listing"
import { iterationCopy } from "~/scripts/clone"

const defaultCoords = {
  lat: null,
  lng: null
}

export const defaultAddress = {
  postcode: null,
  postcode_short: null,
  addressLine1: null,
  addressLine2: null,
  addressLine3: null,
  city: null,
  region: null,
  country: null,
  coordinates: {
    ...defaultCoords
  },
  agglomeration: null,
  place: {}
}
export const state = (): Listing => ({
  id: null,
  uuid: null,

  pickup: {
    address: { ...defaultAddress },
    propertyType: null,
    floor: FLOOR_GROUND,
    liftAvailable: null
  },

  delivery: {
    address: { ...defaultAddress },
    propertyType: null,
    floor: FLOOR_GROUND,
    liftAvailable: null
  },

  category_id: null,
  pm: {
    af: null,
    ref_id: null,
    ref: null
  },

  items: [],
  itemsInitial: true,
  item_identifier_text: "",
  item_identifier_status: null,
  vehicle_is_operational: true
})

export const GET_ITEMS = "getItems"
export const GET_ITEMS_BY_FAKE_ID = "getItemsByFakeID"
export const GET_ITEMS_COUNT = "getItemsCount"
export const GET_UNIQUE_ITEMS_COUNT = "getUniqueItemsCount"
export const GET_FIRST_ITEM = "getFirstItem"
export const GET_ITEM_INDEX = "getItemIndex"
export const GET_ITEM = "getItem"
export const GET_ITEM_IDENTIFIER_TEXT = "getItemIdentifierText"
export const IS_VALID_PICKUP = "isValidPickup"
export const IS_VALID_DELIVERY = "isValidDelivery"
export const GET_PICKUP_ADDRESS = "getPickupAddress"
export const GET_PICKUP_CITY = "getPickupCity"
export const GET_DELIVERY_ADDRESS = "getDeliveryAddress"
export const GET_PICKUP_PROPERTY_TYPE = "getPickupPropertyType"
export const GET_DELIVERY_PROPERTY_TYPE = "getDeliveryPropertyType"
export const GET_PICKUP_FLOOR = "getPickupFloor"
export const GET_DELIVERY_FLOOR = "getDeliveryFloor"
export const GET_DELIVERY_LIFT_AVAILABLE = "getDeliveryLiftAvailable"
export const GET_PICKUP_LIFT_AVAILABLE = "getPickupLiftAvailable"
export const GET_ROUTE_FROM = "getRouteFrom"
export const GET_ROUTE_TO = "getRouteTo"
export const GET_CATEGORY = "getCategory"
export const GET_PARTNER = "getPartner"
export const IS_VEHICLE_OPERATIONAL = "isVehicleOperational"
export const GET_LISTING = "getListing"
export const GET_FAKE_ID = item => {
  return md5(
    item.name +
      (item.original_width ? Number(item.original_width) : Number(item.width)) +
      (item.original_depth ? Number(item.original_depth) : Number(item.depth)) +
      (item.original_height ? Number(item.original_height) : Number(item.height)) +
      item.dimension_unit +
      (item.original_weight ? Number(item.original_weight) : Number(item.weight)) +
      item.weight_unit +
      (item.location_id || item.location_id == 0 ? item.location_id : "")
  )
}

export const getters = {
  [GET_ITEMS]: (state: Listing): Item[] => state.items,
  [GET_ITEMS_BY_FAKE_ID]: (state: Listing): any => {
    const itemsByFakeId = {}
    state.items.forEach(item => {
      if (item.fake_id) itemsByFakeId[item.fake_id] = item
    })
    return itemsByFakeId
  },
  [GET_ITEMS_COUNT]: (state: Listing) =>
    state.itemsInitial
      ? null
      : state.items.map(item => item.quantity).reduce((sum, x) => sum! + (x as number), 0),
  [GET_UNIQUE_ITEMS_COUNT]: (state: Listing) =>
    state.itemsInitial ? null : state.items.reduce(sum => sum! + 1, 0),
  [GET_FIRST_ITEM]: (state: Listing) => state.items.length > 0 && state.items[0],
  [GET_ITEM_INDEX]:
    (state: Listing) =>
    (item: Item): number =>
      state.items.findIndex(it => it.fake_id === item.fake_id),
  [GET_ITEM]: (state: Listing) => (item: Item) =>
    state.items.find(it => it.fake_id === item.fake_id),
  [IS_VALID_PICKUP]: (state: Listing): boolean =>
    state.pickup !== null &&
    typeof state.pickup === "object" &&
    state.pickup.address !== null &&
    typeof state.pickup.address.coordinates === "object" &&
    typeof state.pickup.address.coordinates.lat === "number" &&
    typeof state.pickup.address.coordinates.lng === "number",
  [IS_VALID_DELIVERY]: (state: Listing): boolean =>
    state.delivery !== null &&
    typeof state.delivery === "object" &&
    state.delivery.address !== null &&
    typeof state.delivery.address.coordinates === "object" &&
    typeof state.delivery.address.coordinates.lat === "number" &&
    typeof state.delivery.address.coordinates.lng === "number",
  [GET_PICKUP_ADDRESS]: (state: Listing): Address | null => state.pickup.address,
  [GET_PICKUP_CITY]: (state: Listing): string | null => state.pickup.address ? state.pickup.address.city : null,
  [GET_DELIVERY_ADDRESS]: (state: Listing): Address | null => state.delivery.address,
  [GET_PICKUP_PROPERTY_TYPE]: (state: Listing): string | null => state.pickup.propertyType,
  [GET_DELIVERY_PROPERTY_TYPE]: (state: Listing): string | null => state.delivery.propertyType,
  [GET_PICKUP_FLOOR]: (state: Listing): string | null => state.pickup.floor,
  [GET_DELIVERY_FLOOR]: (state: Listing): string | null => state.delivery.floor,
  [GET_PICKUP_LIFT_AVAILABLE]: (state: Listing): LiftAvailable => state.pickup.liftAvailable,
  [GET_DELIVERY_LIFT_AVAILABLE]: (state: Listing): LiftAvailable => state.delivery.liftAvailable,
  [GET_ROUTE_FROM]: (state: Listing): string => state.pickup.address?.postcode
    ? state.pickup.address?.postcode.split(" ")[0]
    : state.pickup.address?.city
      ? state.pickup.address?.city
      : "",
  [GET_ROUTE_TO]: (state: Listing): string => state.delivery.address?.postcode
    ? state.delivery.address?.postcode.split(" ")[0]
    : state.delivery.address?.city
      ? state.delivery.address?.city
      : "",
  [GET_CATEGORY]: (state: Listing): CategoryID | null => state.category_id,
  [GET_PARTNER]: (state: Listing): ListingPartner => state.pm,
  [IS_VEHICLE_OPERATIONAL]: (state: Listing): boolean | null => state.vehicle_is_operational,
  [GET_LISTING]: (state: Listing): Listing => ({
    ...state,
    pickup: {
      ...state.pickup,
      address: {
        ...state.pickup.address,
        coordinates: { ...state.pickup.address.coordinates },
        place: { ...state.pickup.address.place }
      }
    },
    delivery: {
      ...state.delivery,
      address: {
        ...state.delivery.address,
        coordinates: { ...state.delivery.address.coordinates },
        place: { ...state.delivery.address.place }
      }
    },
    moveDate: state.moveDate
  }),
  [GET_ITEM_IDENTIFIER_TEXT]: (state: Listing): string => state.item_identifier_text
}

export const _ADD_ITEM = "addItem"
export const _UPDATE_ITEM = "updateItem"
export const _DELETE_ITEM = "deleteItem"
export const _SET_ITEMS = "setItems"
export const _SET_ITEM_IDENTIFIER_TEXT = "setItemIdentifierText"
export const _SET_ITEM_IDENTIFIER_STATUS = "setItemIdentifierStatus"
export const _SET_FIRST_ITEM = "setFirstItem"
export const _DELETE_ITEM_BY_INDEX = "deleteItemByIndex"
export const _CLEAR_ITEMS = "clearItems"
export const _SET_PICKUP_ADDRESS = "setPickupAddress"
export const _SET_DELIVERY_ADDRESS = "setDeliveryAddress"
export const _SET_PICKUP_PROPERTY_TYPE = "setPickupPropertyType"
export const _SET_MOVE_DATE = "setMoveDate"
export const _SET_DELIVERY_PROPERTY_TYPE = "setDeliveryPropertyType"
export const _SET_PICKUP_FLOOR = "setPickupFloor"
export const _SET_DELIVERY_FLOOR = "setDeliveryFloor"
export const _SET_PICKUP_LIFT_AVAILABLE = "setPickupLiftAvailable"
export const _SET_DELIVERY_LIFT_AVAILABLE = "setDeliveryLiftAvailable"
export const _SET_CATEGORY = "setCategory"
export const _SET_PARTNER = "setPartner"
export const _SET_PARTNER_AFFILIATE_IDENT = "setPartnerAffiliateIdent"
export const _SET_PARTNER_AFFILIATE_STORE_ID = "setPartnerAffiliateStoreId"
export const _SET_PARTNER_AFFILIATE_STORE_REF = "setPartnerAffiliateStoreRef"
export const _SET_VEHICLE_OPERATIONAL = "setVehicleOptional"
export const _SET_LISTING = "setListing"

export const mutations = {
  [_ADD_ITEM](state: Listing, item: Item) {
    if (typeof item.quantity === "undefined") {
      item.quantity = 1
    }

    state.items.push(item)
    state.itemsInitial = false
  },

  [_UPDATE_ITEM](state: Listing, item: Item) {
    const index: number = state.items.findIndex(
      it => it.fake_id === item.fake_id && it.location_id === item.location_id
    )

    item.fake_id = GET_FAKE_ID(item)
    state.items[index] = item
  },

  [_DELETE_ITEM](state: Listing, item: Item) {
    state.items = state.items.filter(it => it.fake_id !== item.fake_id)
  },

  [_SET_FIRST_ITEM](state: Listing, item: Item) {
    state.items[0] = item
  },

  [_DELETE_ITEM_BY_INDEX](state: Listing, index) {
    state.items.splice(index, 1)
  },

  [_CLEAR_ITEMS](state: Listing) {
    state.items = []
  },

  [_SET_ITEMS](state: Listing, items: Array<Item>) {
    state.items = items
  },

  [_SET_ITEM_IDENTIFIER_TEXT](state: Listing, item_identifier_text: string) {
    state.item_identifier_text = item_identifier_text
  },

  [_SET_ITEM_IDENTIFIER_STATUS](state: Listing, item_identifier_status: number) {
    state.item_identifier_status = item_identifier_status
  },

  [_SET_PICKUP_ADDRESS](state: Listing, address: Address | null) {
    state.pickup.address = address
  },
  [_SET_DELIVERY_ADDRESS](state: Listing, address: Address | null) {
    state.delivery.address = address
  },

  [_SET_PICKUP_PROPERTY_TYPE](state: Listing, propertyType: string) {
    state.pickup.propertyType = propertyType
  },

  [_SET_MOVE_DATE](state: Listing, moveDate: string | null) {
    state.moveDate = moveDate
  },

  [_SET_DELIVERY_PROPERTY_TYPE](state: Listing, propertyType: string) {
    state.delivery.propertyType = propertyType
  },

  [_SET_PICKUP_FLOOR](state: Listing, floor: string) {
    state.pickup.floor = floor
  },

  [_SET_DELIVERY_FLOOR](state: Listing, floor: string) {
    state.delivery.floor = floor
  },

  [_SET_PICKUP_LIFT_AVAILABLE](state: Listing, lift: LiftAvailable) {
    state.pickup.liftAvailable = lift
  },

  [_SET_DELIVERY_LIFT_AVAILABLE](state: Listing, lift: LiftAvailable) {
    state.delivery.liftAvailable = lift
  },

  [_SET_CATEGORY](state: Listing, categoryId: string | null) {
    state.category_id = categoryId
  },

  [_SET_PARTNER](state: Listing, partner: ListingPartner) {
    state.pm = partner
  },
  [_SET_PARTNER_AFFILIATE_IDENT](state: Listing, affiliateIdent: string) {
    state.pm.af = affiliateIdent
  },
  [_SET_PARTNER_AFFILIATE_STORE_ID](state: Listing, storeId: string) {
    state.pm.ref_id = storeId
  },
  [_SET_PARTNER_AFFILIATE_STORE_REF](state: Listing, storeRef: string) {
    state.pm.ref = storeRef
  },

  [_SET_VEHICLE_OPERATIONAL](state: Listing, isVehicleOperational: boolean | null) {
    state.vehicle_is_operational = isVehicleOperational
  },

  [_SET_LISTING](state: Store<Listing>, listing: Listing) {
    for (const prop in state) {
      if (listing[prop] !== undefined) {
        state[prop] = listing[prop]
      }
    }
  }
}

export const ADD_ITEM = "addItem"
export const UPDATE_ITEM = "updateItem"
export const DELETE_ITEM = "deleteItem"
export const INCREASE_ITEM = "increaseItem"
export const DECREASE_ITEM = "decreaseItem"
export const SET_FIRST_ITEM = "setFirstItem"
export const DELETE_ITEM_BY_INDEX = "deleteItemByIndex"
export const SET_ITEMS = "setItems"
export const SET_ITEM_IDENTIFIER_TEXT = "setItemIdentifierText"
export const SET_ITEM_IDENTIFIER_STATUS = "setItemIdentifierStatus"
export const CLEAR_ITEMS = "clearItems"
export const UPDATE_ITEMS = "updateItems"
export const ADD_ITEM_WITHOUT_REQUEST = "addItemWithoutRequest"
export const UPDATE_ITEM_WITHOUT_REQUEST = "updateItemWithoutRequest"
export const SET_PICKUP_ADDRESS = "setPickupAddress"
export const SET_DELIVERY_ADDRESS = "setDeliveryAddress"
export const SET_PICKUP_PROPERTY_TYPE = "setPickupPropertyType"
export const SET_MOVE_DATE = "setMoveDate"
export const SET_DELIVERY_PROPERTY_TYPE = "setDeliveryPropertyType"
export const SET_PICKUP_FLOOR = "setPickupFloor"
export const SET_DELIVERY_FLOOR = "setDeliveryFloor"
export const SET_PICKUP_LIFT_AVAILABLE = "setPickupLiftAvailable"
export const SET_DELIVERY_LIFT_AVAILABLE = "setDeliveryLiftAvailable"
export const SET_CATEGORY = "setCategory"
export const SET_PARTNER = "setPartner"
export const SET_PARTNER_AFFILIATE_IDENT = "setPartnerAffiliateIdent"
export const SET_PARTNER_AFFILIATE_STORE_ID = "setPartnerAffiliateStoreId"
export const SET_PARTNER_AFFILIATE_STORE_REF = "setPartnerAffiliateStoreRef"
export const SET_VEHICLE_OPERATIONAL = "setVehicleOptional"
export const SET_LISTING = "setListing"

export const actions = {
  [ADD_ITEM]({ commit, getters }, itemObj: Item) {
    const item: Item = Object.assign({}, itemObj)
    item.fake_id = GET_FAKE_ID(item)
    const alreadyAddedItem: Item = getters[GET_ITEM](item)

    if (typeof alreadyAddedItem !== "undefined") {
      item.quantity = alreadyAddedItem.quantity! + (item.quantity ? item.quantity : 1)
      item.item_id = alreadyAddedItem.item_id!
      commit(_UPDATE_ITEM, item)
    } else {
      if (!item.quantity) {
        item.quantity = 1
      }
      const itemSaved: Item = Object.assign({}, item)
      commit(_ADD_ITEM, itemSaved)
    }

    return item
  },

  [UPDATE_ITEM]({ commit, getters }, itemObj: Item) {
    const item: Item = Object.assign({}, itemObj)
    item.fake_id = GET_FAKE_ID(item)

    const alreadyAddedItem: Item | undefined = getters[GET_ITEM](item)

    if (typeof alreadyAddedItem !== "undefined" && itemObj.fake_id !== item.fake_id) {
      item.quantity = alreadyAddedItem.quantity! + (item.quantity ? item.quantity : 1)
      commit(_UPDATE_ITEM, item)
      commit(_DELETE_ITEM, itemObj)
      return true
    }

    const alreadyAddedItemObj: Item | undefined = getters[GET_ITEM](itemObj)

    if (typeof alreadyAddedItemObj !== "undefined") {
      commit(_UPDATE_ITEM, itemObj)
      return true
    }
  },

  [DELETE_ITEM]({ commit, getters }, itemObj: Item) {
    if (typeof getters[GET_ITEM](itemObj) !== "undefined") {
      commit(_DELETE_ITEM, itemObj)
      return true
    }
  },

  [INCREASE_ITEM]({ commit, getters }, itemObj: Item) {
    if (typeof getters[GET_ITEM](itemObj) !== "undefined") {
      const item: Item = Object.assign({}, itemObj)
      item.quantity!++
      commit(_UPDATE_ITEM, item)
      return true
    }
  },

  [DECREASE_ITEM]({ commit, getters }, itemObj: Item) {
    if (typeof getters[GET_ITEM](itemObj) !== "undefined") {
      if (itemObj.quantity !== undefined && itemObj.quantity > 1) {
        const item: Item = Object.assign({}, itemObj)
        item.quantity!--
        commit(_UPDATE_ITEM, item)
      } else {
        commit(_DELETE_ITEM, itemObj)
        return true
      }
    }
  },

  [SET_FIRST_ITEM]({ commit }, item: Item) {
    commit(_SET_FIRST_ITEM, iterationCopy(item))
  },

  [DELETE_ITEM_BY_INDEX]({ commit }, index: number) {
    commit(_DELETE_ITEM_BY_INDEX, index)
  },

  [CLEAR_ITEMS]({ commit }) {
    commit(_CLEAR_ITEMS)
  },

  [SET_ITEMS]({ commit }, items: Array<Item>) {
    commit(_SET_ITEMS, items)
  },

  [SET_ITEM_IDENTIFIER_TEXT]({ commit }, item_identifier_text: string) {
    commit(_SET_ITEM_IDENTIFIER_TEXT, item_identifier_text)
  },

  [SET_ITEM_IDENTIFIER_STATUS]({ commit }, item_identifier_status: number) {
    commit(_SET_ITEM_IDENTIFIER_STATUS, item_identifier_status)
  },

  [ADD_ITEM_WITHOUT_REQUEST]({ commit }, item: Item) {
    commit(_ADD_ITEM, item)
  },

  [UPDATE_ITEM_WITHOUT_REQUEST]({ commit }, item: Item) {
    commit(_UPDATE_ITEM, item)
  },

  [SET_PICKUP_ADDRESS]({ commit }, address: Address | null) {
    commit(_SET_PICKUP_ADDRESS, address)
  },

  [SET_DELIVERY_ADDRESS]({ commit }, address: Address | null) {
    commit(_SET_DELIVERY_ADDRESS, address)
  },

  [SET_PICKUP_PROPERTY_TYPE]({ commit }, propertyType: string | null) {
    commit(_SET_PICKUP_PROPERTY_TYPE, propertyType)
  },

  [SET_MOVE_DATE]({ commit }, moveDate: string | null) {
    commit(_SET_MOVE_DATE, moveDate)
  },

  [SET_DELIVERY_PROPERTY_TYPE]({ commit }, propertyType: string | null) {
    commit(_SET_DELIVERY_PROPERTY_TYPE, propertyType)
  },

  [SET_PICKUP_FLOOR]({ commit }, floor: string | null) {
    commit(_SET_PICKUP_FLOOR, floor)
  },

  [SET_DELIVERY_FLOOR]({ commit }, floor: string | null) {
    commit(_SET_DELIVERY_FLOOR, floor)
  },

  [SET_PICKUP_LIFT_AVAILABLE]({ commit }, lift: LiftAvailable) {
    commit(_SET_PICKUP_LIFT_AVAILABLE, lift)
  },

  [SET_DELIVERY_LIFT_AVAILABLE]({ commit }, lift: LiftAvailable) {
    commit(_SET_DELIVERY_LIFT_AVAILABLE, lift)
  },

  [SET_CATEGORY]({ commit }, categoryId: string | null) {
    commit(_SET_CATEGORY, categoryId)
  },

  [SET_PARTNER]({ commit }, partner: ListingPartner) {
    commit(_SET_PARTNER, partner)
  },
  [SET_PARTNER_AFFILIATE_IDENT]({ commit }, affiliateIdent: string) {
    commit(_SET_PARTNER_AFFILIATE_IDENT, affiliateIdent)
  },
  [SET_PARTNER_AFFILIATE_STORE_ID]({ commit }, storeId: string) {
    commit(_SET_PARTNER_AFFILIATE_STORE_ID, storeId)
  },
  [SET_PARTNER_AFFILIATE_STORE_REF]({ commit }, storeRef: string) {
    commit(_SET_PARTNER_AFFILIATE_STORE_REF, storeRef)
  },

  [SET_VEHICLE_OPERATIONAL]({ commit }, isVehicleOperational: boolean | null) {
    commit(_SET_VEHICLE_OPERATIONAL, isVehicleOperational)
  },

  [SET_LISTING]({ commit }, listing: Listing) {
    commit(_SET_LISTING, listing)
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
